import React from 'react';
import { 
  Box, 
  Typography, 
  Card, 
  Paper, 
  Grid,
  Container,
  Avatar,
  Chip,
  Divider
} from '@mui/material';
import { Engineering, School, WorkspacePremium } from '@mui/icons-material';

const NuestroEquipo = () => {
  const teamMembers = [
    {
      name: 'Ing. Jesús Ortíz Yépez',
      position: 'Director General',
      experience: '20 años',
      specialties: ['Protección Civil', 'Gestión de Riesgos'],
      education: 'Maestría en Protección Civil y Emergencias',
      image: '/images/equipo/jesus.jpg',
      description: 'Líder visionario con más de 20 años de experiencia en el sector de protección civil y gestión de riesgos.',
      highlight: true
    },
    {
      name: 'Ing. María González',
      position: 'Gerente de Proyectos',
      experience: '12 años',
      specialties: ['Gestión de Proyectos', 'Coordinación de Equipos'],
      education: 'Ingeniería Civil - UNAM',
      image: '/images/equipo/maria.jpg',
      description: 'Especialista en gestión de proyectos complejos y coordinación de equipos multidisciplinarios.'
    },
    {
      name: 'Arq. Roberto Silva',
      position: 'Director Técnico',
      experience: '15 años',
      specialties: ['Normatividad', 'Supervisión de Obras'],
      education: 'Arquitectura - IPN',
      image: '/images/equipo/roberto.jpg',
      description: 'Arquitecto con amplia experiencia en diseño, supervisión de obras y normatividad de protección civil.'
    },
    {
      name: 'Lic. Ana Rodríguez',
      position: 'Gerente Administrativa',
      experience: '10 años',
      specialties: ['Administración', 'Recursos Humanos'],
      education: 'Licenciatura en Administración - UAM',
      image: '/images/equipo/ana.jpg',
      description: 'Responsable de la gestión administrativa, financiera y recursos humanos de la organización.'
    }
  ];

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box textAlign="center" mb={6}>
        <Typography variant="h3" color="primary" fontWeight="bold" gutterBottom>
          Nuestro Equipo
        </Typography>
        <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 700, mx: 'auto' }}>
          Profesionales altamente capacitados comprometidos con la excelencia
        </Typography>
      </Box>

      {/* Estadísticas del equipo */}
      <Grid container spacing={3} mb={6}>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Card elevation={4} sx={{ p: 3, textAlign: 'center', borderRadius: 3, bgcolor: 'primary.main', color: 'white' }}>
            <Engineering sx={{ fontSize: 48, mb: 2 }} />
            <Typography variant="h4" fontWeight="bold">15+</Typography>
            <Typography variant="h6">Ingenieros</Typography>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Card elevation={4} sx={{ p: 3, textAlign: 'center', borderRadius: 3, bgcolor: 'success.main', color: 'white' }}>
            <WorkspacePremium sx={{ fontSize: 48, mb: 2 }} />
            <Typography variant="h4" fontWeight="bold">50+</Typography>
            <Typography variant="h6">Certificaciones</Typography>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Card elevation={4} sx={{ p: 3, textAlign: 'center', borderRadius: 3, bgcolor: 'warning.main', color: 'white' }}>
            <School sx={{ fontSize: 48, mb: 2 }} />
            <Typography variant="h4" fontWeight="bold">100+</Typography>
            <Typography variant="h6">Años de Experiencia</Typography>
          </Card>
        </Grid>
      </Grid>

      {/* Equipo directivo */}
      <Paper elevation={6} sx={{ p: 4, borderRadius: 4, mb: 6 }}>
        <Typography variant="h4" color="primary" fontWeight="bold" textAlign="center" gutterBottom>
          Equipo Directivo
        </Typography>
        <Divider sx={{ mb: 4, bgcolor: 'primary.main', height: 2 }} />
        
        <Grid container spacing={4} alignItems="stretch">
          {teamMembers.map((member, index) => (
            <Grid size={{ xs: 12, md: member.highlight ? 12 : 4 }} key={index} sx={{ display: 'flex' }}>
              <Card 
                elevation={4} 
                sx={{ 
                  p: 3, 
                  borderRadius: 3, 
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'transform 0.3s ease-in-out',
                  border: member.highlight ? '2px solid #667eea' : 'none',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: 8
                  }
                }}
              >
                <Box display="flex" alignItems="center" mb={3}>
                  <Avatar
                    src={member.image}
                    sx={{ 
                      width: 80, 
                      height: 80, 
                      mr: 3,
                      bgcolor: 'primary.main',
                      fontSize: '2rem'
                    }}
                    onError={(e) => {
                      e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(member.name)}&background=1976d2&color=fff&size=80`;
                    }}
                  >
                    {member.name.split(' ').map(n => n[0]).join('')}
                  </Avatar>
                  <Box>
                    <Typography variant="h6" fontWeight="bold" color="primary">
                      {member.name}
                    </Typography>
                    <Typography variant="subtitle1" color="text.secondary" fontWeight="500">
                      {member.position}
                    </Typography>
                    <Chip 
                      label={member.experience} 
                      size="small" 
                      color="primary" 
                      variant="outlined"
                      sx={{ mt: 1 }}
                    />
                  </Box>
                </Box>
                
                <Typography variant="body1" color="text.secondary" paragraph sx={{ lineHeight: 1.6 }}>
                  {member.description}
                </Typography>
                
                <Box mb={2}>
                  <Typography variant="subtitle2" color="primary" fontWeight="bold" gutterBottom>
                    Especialidades:
                  </Typography>
                  <Box display="flex" flexWrap="wrap" gap={1}>
                    {member.specialties.map((specialty, idx) => (
                      <Chip 
                        key={idx}
                        label={specialty} 
                        size="small" 
                        variant="outlined"
                        color="secondary"
                      />
                    ))}
                  </Box>
                </Box>
                
                <Box display="flex" alignItems="center" mt={2}>
                  <School sx={{ fontSize: 20, color: 'text.secondary', mr: 1 }} />
                  <Typography variant="body2" color="text.secondary">
                    {member.education}
                  </Typography>
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Paper>

      {/* Valores del equipo */}
      <Paper 
        elevation={6} 
        sx={{ 
          p: 4, 
          borderRadius: 4,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white'
        }}
      >
        <Typography variant="h4" fontWeight="bold" textAlign="center" gutterBottom>
          Nuestros Valores de Equipo
        </Typography>
        <Divider sx={{ mb: 4, bgcolor: 'white', height: 2 }} />
        
        <Grid container spacing={4}>
          {[
            { 
              title: 'Colaboración', 
              description: 'Trabajamos unidos hacia objetivos comunes, aprovechando las fortalezas individuales.' 
            },
            { 
              title: 'Innovación', 
              description: 'Fomentamos la creatividad y el pensamiento disruptivo en cada proyecto.' 
            },
            { 
              title: 'Excelencia', 
              description: 'Nos comprometemos con los más altos estándares de calidad profesional.' 
            },
            { 
              title: 'Integridad', 
              description: 'Actuamos con transparencia y ética en todas nuestras decisiones.' 
            }
          ].map((valor, index) => (
            <Grid size={{ xs: 12, sm: 6 }} key={index}>
              <Box textAlign="center" p={2}>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  {valor.title}
                </Typography>
                <Typography variant="body1" sx={{ opacity: 0.9, lineHeight: 1.6 }}>
                  {valor.description}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Paper>
    </Container>
  );
};

export default NuestroEquipo;
